import { useTranslation } from "react-i18next";
import { Button, Input, Icon } from "@thetreep/ui";
import { getColor } from "@utils/colors";
import { BookingType } from "@gen/dap/v1/dap_pb";
import { useEffect, useState, startTransition } from "react";
import { BannerMessage } from "@components/common/BannerMessage";
import { useSearchParams } from "react-router";
import { DAP } from "@gen/dap/v1/dap_pb";
import { useUserStore } from "@stores/userStore";
import { Role } from "@gen/user/v1/user_pb";

export function DAPNumber({
  dap,
  isLoading,
  errorMessage,
}: {
  dap: DAP | undefined;
  isLoading: boolean;
  errorMessage?: string;
}) {
  const { t } = useTranslation();
  const [dapNumberInputValue, setDapNumberInputValue] = useState<string>("");
  const [dapNameInputValue, setDapNameInputValue] = useState<string>("");
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useUserStore();

  const ASK_FOR_BENEFICIARY_NAME = user?.role === Role.USER;

  function handleSubmit() {
    startTransition(() => {
      if (dap) {
        searchParams.delete("dap");
        setSearchParams(searchParams);
        return;
      }

      if (
        dapNumberInputValue &&
        (!ASK_FOR_BENEFICIARY_NAME || dapNameInputValue)
      ) {
        searchParams.set("dap", dapNumberInputValue);
        searchParams.set("dap_name", dapNameInputValue);
        setSearchParams(searchParams);
      }
    });
  }

  useEffect(() => {
    const dapNumber = searchParams.get("dap");
    const dapName = searchParams.get("dap_name");
    startTransition(() => {
      if (dapNumber) {
        setDapNumberInputValue(dapNumber);
      }
      if (ASK_FOR_BENEFICIARY_NAME && dapName) {
        setDapNameInputValue(dapName);
      }
    });
  }, [searchParams, ASK_FOR_BENEFICIARY_NAME]);

  function message() {
    if (!dap && searchParams.has("dap") && !isLoading) {
      return (
        <BannerMessage className="mt-4" type="error">
          {errorMessage || t("booking.unknownNumber")}
        </BannerMessage>
      );
    }

    if (dap && errorMessage) {
      return (
        <BannerMessage>
          <div className="flex flex-col gap-4">
            <p
              className="whitespace-pre-line"
              dangerouslySetInnerHTML={{ __html: errorMessage }}
            />
          </div>
        </BannerMessage>
      );
    }

    if (dap && dap.type !== BookingType.UNSPECIFIED) {
      return (
        <>
          {/* A caisse comment is why the tool cannot decide on its own. Agents and
              admins are allowed through, so they must be able to read it before
              they commit to the booking. */}
          {dap.comment && (
            <BannerMessage className="mt-4" type="warning">
              <div className="flex flex-col gap-1">
                <p className="font-bold">{t("booking.dapComment")}</p>
                <p className="whitespace-pre-line">{dap.comment}</p>
              </div>
            </BannerMessage>
          )}
          <div className="flex gap-2 items-center mt-2">
            <Icon
              src="check_circle_fill"
              size={16}
              color={getColor("success-strong")}
            />
            <p className="text-success-strong font-bold text-lg">
              {t("booking.validatedNumber")}
            </p>
          </div>
        </>
      );
    }

    return null;
  }

  return (
    <>
      <div>
        <div className="flex gap-6 flex-col md:flex-row">
          <Input
            overrides={{
              Input: {
                props: {
                  "data-testid": "dap-number-input",
                },
              },
            }}
            startEnhancer={<span>N°</span>}
            disabled={!!dap}
            value={dapNumberInputValue}
            onChange={(e) => setDapNumberInputValue(e.target.value)}
          />
          {ASK_FOR_BENEFICIARY_NAME && (
            <Input
              overrides={{
                Input: {
                  props: {
                    "data-testid": "dap-name-input",
                  },
                },
              }}
              startEnhancer={<span>{t("booking.dapName")}</span>}
              disabled={!!dap}
              value={dapNameInputValue}
              onChange={(e) => setDapNameInputValue(e.target.value)}
            />
          )}

          <Button
            data-testid="dap-number-submit"
            onClick={handleSubmit}
            isLoading={isLoading}
          >
            {!dap ? t("validate") : t("modify")}
          </Button>
        </div>
      </div>
      {message()}
    </>
  );
}
